const escapeRegex = (str = "") => str.replace(/[/\-\\^$*+?.()|[\]{}]/g, "\\$&");

/**
 * Standard Department Code Mappings
 * Explicit rules:
 * - HR : hr
 * - Marketing : mkt
 * - Development : dev
 * - IT : it
 * - Management : mngmt
 */
export const DEPARTMENT_CODE_MAP = {
  HR: "hr",
  "HUMAN RESOURCES": "hr",
  MARKETING: "mkt",
  DEVELOPMENT: "dev",
  "SOFTWARE DEVELOPMENT": "dev",
  "R&D": "dev",
  RESEARCH: "rsch",
  IT: "it",
  "INFORMATION TECHNOLOGY": "it",
  TECH: "it",
  MANAGEMENT: "mngmt",
  OPERATIONS: "ops",
  FINANCE: "fin",
  ACCOUNTS: "acc",
  ACCOUNTING: "acc",
  SALES: "sales",
  "BUSINESS DEVELOPMENT": "biz",
  ADMIN: "admin",
  ADMINISTRATION: "admin",
  LEGAL: "legal",
  COMPLIANCE: "comp",
  SECURITY: "sec",
  "QUALITY ASSURANCE": "qa",
  QA: "qa",
  ENGINEERING: "eng",
  CIVIL: "civ",
  PURCHASE: "proc",
  PROCUREMENT: "proc",
  LOGISTICS: "log",
  PRODUCTION: "prod",
  FACILITY: "fac",
  MAINTENANCE: "maint",
  DIRECTOR: "dir",
  EXECUTIVE: "exec",
  RECEPTION: "rec",
  "FRONT DESK": "rec",
  SUPPORT: "supp",
  "CUSTOMER SUPPORT": "cs",
};

/**
 * Auto-generate Department Short Code
 * Guarantees lowercase code, handles synonyms, acronyms, and collision prevention.
 */
export const getDepartmentCode = (name = "", existingCodes = []) => {
  if (!name) return "gen";
  const str = String(name).trim();

  // 1. Acronym in parentheses e.g. "Automation Lab (AL)" -> al
  const parenMatch = str.match(/\(([A-Za-z0-9]{2,5})\)/);
  if (parenMatch) {
    const raw = parenMatch[1].toLowerCase();
    return ensureUniqueCode(raw, existingCodes);
  }

  // 2. Direct dictionary match
  const clean = str.replace(/[^a-zA-Z0-9\s]/g, " ").trim().toUpperCase();
  if (DEPARTMENT_CODE_MAP[clean]) {
    return ensureUniqueCode(DEPARTMENT_CODE_MAP[clean], existingCodes);
  }

  // Partial match in dictionary
  for (const [key, code] of Object.entries(DEPARTMENT_CODE_MAP)) {
    if (clean === key || clean.startsWith(`${key} `) || clean.endsWith(` ${key}`)) {
      return ensureUniqueCode(code, existingCodes);
    }
  }

  // 3. Multi-word: initials (e.g. "Public Relations" -> pr)
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length > 1) {
    const acronym = words.map((w) => w[0].toLowerCase()).join("").slice(0, 4);
    if (acronym.length >= 2) {
      return ensureUniqueCode(acronym, existingCodes);
    }
  }

  // 4. Single-word custom: strip internal vowels or take first 3-4 chars
  const consonants = clean[0] + clean.slice(1).replace(/[AEIOU\s]/g, "");
  const baseCode = (consonants.length >= 3 ? consonants.slice(0, 4) : clean.slice(0, 3)).toLowerCase();
  return ensureUniqueCode(baseCode || "gen", existingCodes);
};

/**
 * Backward compatibility alias
 */
export const getDeptPrefix = (val = "") => {
  return getDepartmentCode(val).toUpperCase();
};

/**
 * Ensure uniqueness against existing codes by appending incremental index (e.g. mkt2, dev2)
 */
export const ensureUniqueCode = (base, existingCodes = []) => {
  const normalizedBase = String(base || "gen").toLowerCase();
  if (!Array.isArray(existingCodes) || !existingCodes.length) {
    return normalizedBase;
  }
  const lowerCodes = existingCodes.map((c) => String(c).toLowerCase());
  if (!lowerCodes.includes(normalizedBase)) {
    return normalizedBase;
  }
  let index = 2;
  while (lowerCodes.includes(`${normalizedBase}${index}`)) {
    index++;
  }
  return `${normalizedBase}${index}`;
};

/**
 * Property Prefix Generator
 * E.g.
 * "AsmitA House" -> "ah"
 * "Main HQ" -> "mhq"
 * "AsmitA Grandeur" -> "ag"
 */
export const getPropertyPrefix = (propertyName = "") => {
  if (!propertyName) return "";
  const clean = String(propertyName)
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim();
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length > 1) {
    return words.map((w) => w[0].toLowerCase()).join("");
  }
  return clean.slice(0, 3).toLowerCase();
};

/**
 * Auto-generate Desk or Cabin Code
 * Format: {propertyPrefix}-{deptCode}-{typePrefix}{01..99}
 * E.g.
 * AsmitA House + HR + desk => ah-hr-d01
 * AsmitA House + Marketing + desk => ah-mkt-d01
 * AsmitA House + Development + cabin => ah-dev-c01
 * AsmitA House + IT + desk => ah-it-d01
 * AsmitA House + Management + cabin => ah-mngmt-c01
 */
export const generateWorkspaceCode = ({
  propertyName = "",
  department = "",
  type = "desk", // "desk" | "cabin"
  existingCodes = [],
  startOffset = 1,
}) => {
  const propPrefix = getPropertyPrefix(propertyName);
  const deptCode = getDepartmentCode(department);
  const typeLetter = type === "cabin" ? "c" : "d";

  const prefix = propPrefix ? `${propPrefix}-${deptCode}-${typeLetter}` : `${deptCode}-${typeLetter}`;

  const regex = new RegExp(`^${escapeRegex(prefix)}(\\d+)$`, "i");
  const usedNums = (existingCodes || [])
    .map((c) => {
      const match = String(c || "").trim().match(regex);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => n > 0);

  const nextNum = usedNums.length > 0 ? Math.max(...usedNums) + 1 : startOffset;
  return `${prefix}${String(nextNum).padStart(2, "0")}`;
};

/**
 * Bulk generate unique sequential workspace codes
 * E.g. ['ah-hr-d01', 'ah-hr-d02', 'ah-hr-d03']
 */
export const generateBulkWorkspaceCodes = ({
  propertyName = "",
  department = "",
  type = "desk",
  count = 1,
  existingCodes = [],
}) => {
  const propPrefix = getPropertyPrefix(propertyName);
  const deptCode = getDepartmentCode(department);
  const typeLetter = type === "cabin" ? "c" : "d";
  const prefix = propPrefix ? `${propPrefix}-${deptCode}-${typeLetter}` : `${deptCode}-${typeLetter}`;

  const regex = new RegExp(`^${escapeRegex(prefix)}(\\d+)$`, "i");
  const usedNums = (existingCodes || [])
    .map((c) => {
      const match = String(c || "").trim().match(regex);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => n > 0);

  const startNum = usedNums.length > 0 ? Math.max(...usedNums) + 1 : 1;
  const results = [];
  for (let i = 0; i < count; i++) {
    results.push(`${prefix}${String(startNum + i).padStart(2, "0")}`);
  }
  return results;
};

/**
 * Legacy Desk Code generator for backward compatibility
 */
export const generateDeskCode = (dept = "", existingCodes = []) => {
  return generateWorkspaceCode({ department: dept, type: "desk", existingCodes });
};

/**
 * Legacy Cabin Code generator for backward compatibility
 */
export const generateCabinCode = (dept = "", existingCodes = []) => {
  return generateWorkspaceCode({ department: dept, type: "cabin", existingCodes });
};
